import {
	faCircleExclamation,
	faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	buildBillingLine,
	buildNextChargeLine,
	formatBillingDate,
} from '@supply-mover-2-0/core';
import type {
	PortalAccountScope,
	PortalBillingSnapshotDto,
} from '@supply-mover-2-0/shared';
import { Select, useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthGate } from '../../../hooks/useAuthGate';
import { useCanMakePayment } from '../../../hooks/useCanMakePayment';
import { useDisplaySettings } from '../../../hooks/useDisplaySettings';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontAccountSnapshot } from '../../../services/portalDashboardService';
import { SnapshotMetricsSkeleton } from '../../skeletons';

/**
 * Account Snapshot widget — open balance, credit limit, available
 * credit, optional credit-hold flag. Two CTA links route to the
 * Invoices and Account pages where the customer can drill in.
 *
 * Login-required. For anonymous visitors the widget renders a sign-in
 * prompt rather than disappearing — the homepage stays usable for
 * browsing, and the widget doubles as a sign-in conversion surface.
 */
export function AccountSnapshotWidget({
	title,
}: {
	title?: string;
}): JSX.Element | null {
	const { t } = useTranslation();
	const requireAuth = useAuthGate();
	const navigate = useNavigate();
	const display = useDisplaySettings();
	const { token, activeClientId } = usePortalAuth();
	const canMakePayment = useCanMakePayment();

	// PD-14239: open balance / credit / account funds are purely financial —
	// don't render for hide-pricing users (server forces canViewInvoices off).
	// Defensive: the dispatch already skips mounting this widget.
	const canViewInvoices = display.canViewInvoices;

	// PD-14188: roll-up scope (current account / parent umbrella / ownership
	// umbrella). Reset to the active account whenever the visitor switches
	// accounts — the new account may belong to different (or no) umbrellas.
	const [scope, setScope] = useState<PortalAccountScope>('current');
	useEffect(() => {
		setScope('current');
	}, [activeClientId]);

	const { data, isLoading, isError } = useQuery({
		// Key includes the active client + scope so the widget refetches on an
		// account switch (PD-14188 — the old key omitted activeClientId and
		// went stale) and on a roll-up change.
		queryKey: [
			'portal',
			'dashboard',
			'account-snapshot',
			activeClientId,
			scope,
		],
		queryFn: () => getStorefrontAccountSnapshot(token, scope),
		enabled: token != null,
		staleTime: 60_000,
	});

	const snapshot = data?.data ?? null;
	const availableScopes = snapshot?.availableScopes ?? [];

	// PD-14256: the dropdown lists the top-level entries (current account +
	// umbrella roll-ups) first, then each individual child / ownership account
	// under its own heading — same grouping language as the Location picker.
	// PD-17144: the shared Select (not a native <select>, which can only draw
	// one line per option) so each account shows its customer ID on a second
	// line; HF's account names are long enough that appending the ID would cut
	// it off. Roll-ups show how many accounts they cover there instead.
	const groupLabel: Record<'child' | 'ownership', string> = {
		child: t('Child Accounts'),
		ownership: t('Ownership Accounts'),
	};
	const scopeOptions = [
		...availableScopes.filter((opt) => opt.group == null),
		...availableScopes.filter((opt) => opt.group === 'child'),
		...availableScopes.filter((opt) => opt.group === 'ownership'),
	].map((opt) => ({
		value: opt.scope,
		label: t(opt.label),
		subtitle:
			opt.customerId != null
				? t(opt.customerId)
				: opt.accountCount > 1
					? t('{{count}} accounts', { count: opt.accountCount })
					: undefined,
		group: opt.group != null ? groupLabel[opt.group] : undefined,
	}));

	// PD-14239: bail after the hooks (rules-of-hooks) so hide-pricing users
	// never see balances, credit, or account funds.
	if (!canViewInvoices) {
		return null;
	}

	return (
		<section className="w-full h-full flex flex-col bg-brand-surface border border-brand-border rounded-xl p-4 md:p-5">
			<header className="flex items-center justify-between gap-2 mb-4">
				<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-default">
					<FontAwesomeIcon icon={faUser} className="text-portal-accent" />
					{t(title ?? 'Account Balance & Credit')}
				</h2>
				{/* PD-14188: roll-up scope picker — only when the active account
				    belongs to a parent/ownership umbrella the visitor can see.
				    PD-14256: also lists each individual child / ownership account
				    so the customer can view any one account's balance & credit. */}
				{availableScopes.length > 1 && (
					<div className="min-w-0 max-w-[55%]">
						<Select
							aria-label={t('Roll up account summary')}
							size="sm"
							value={scope}
							onChange={(next) => {
								setScope(next as PortalAccountScope);
							}}
							options={scopeOptions}
						/>
					</div>
				)}
			</header>

			{token == null ? (
				<div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
					<p className="text-sm text-brand-muted max-w-xs">
						{t('Sign in to see your balance, credit, and recent activity.')}
					</p>
					<button
						type="button"
						onClick={() => requireAuth()}
						className="px-4 py-2 text-sm font-medium rounded-md bg-portal-accent text-white hover:opacity-90"
					>
						{t('Sign in')}
					</button>
				</div>
			) : isLoading ? (
				/* Shaped like the metric grid below so numbers land without a
				   jump (PD-14692). */
				<SnapshotMetricsSkeleton />
			) : isError || snapshot == null ? (
				<EmptyMessage message={t("We couldn't load your account right now.")} />
			) : (
				<>
					<div className="flex-1 grid grid-cols-2 gap-4">
						{/* PD-14239: openBalance is null when the active client hides
						    pricing — guard like the other money metrics (the whole
						    widget is also hidden via canViewInvoices, this keeps the
						    type honest + avoids a crash if it ever renders). */}
						<MetricBlock
							label={t('Open balance')}
							value={
								snapshot.openBalance != null
									? formatCurrency(snapshot.openBalance, t)
									: t('—')
							}
						/>
						{/* PD-14899: the slice of the open balance already past its
						    due date. Red when anything is overdue; a $0.00 stays
						    neutral — it's reassurance, not an alert. */}
						<MetricBlock
							label={t('Past due balance')}
							value={
								snapshot.pastDueBalance != null
									? formatCurrency(snapshot.pastDueBalance, t)
									: t('—')
							}
							valueClassName={
								snapshot.pastDueBalance != null && snapshot.pastDueBalance > 0
									? 'text-danger'
									: undefined
							}
						/>
						{/* PD-14899: per-brand opt-out for tenants that don't manage
						    credit limits in Workd (the metric would always be "—"). */}
						{display.showAvailableCredit && (
							<MetricBlock
								label={t('Available credit')}
								value={
									snapshot.availableCredit != null
										? formatCurrency(snapshot.availableCredit, t)
										: t('—')
								}
								sublabel={
									snapshot.creditLimit != null
										? `${t('Limit')}: ${formatCurrency(snapshot.creditLimit, t)}`
										: undefined
								}
							/>
						)}
						{/* PD-13704: surface the customer's on-account funds (money
						    paid that isn't applied to an invoice). Shown only when
						    they have a balance, so accounts with none aren't cluttered. */}
						{snapshot.accountFunds != null && snapshot.accountFunds > 0 && (
							<MetricBlock
								label={t('Available funds')}
								value={formatCurrency(snapshot.accountFunds, t)}
							/>
						)}
					</div>

					{/* PD-15869 — how this account is billed, and what its next
					    charge is. Absent entirely for a tenant that has not
					    enabled billing modes, which is the isolation guarantee
					    of PD-15708 §0. */}
					{snapshot.billing.enabled && (
						<BillingBlock billing={snapshot.billing} t={t} />
					)}

					{snapshot.hasCreditHold && (
						<div className="mt-3 p-2 rounded-md flex items-start gap-2 text-xs bg-warning-badge text-warning-tint-fg border border-warning-tint">
							<FontAwesomeIcon icon={faCircleExclamation} className="mt-0.5" />
							<span>
								{t(
									'Your account is currently on credit hold. Contact your sales rep for details.',
								)}
							</span>
						</div>
					)}

					{/* PD-14691: the balance's natural next step, one tap from the
					    balance it settles. PD-16789: shown only when there is
					    something to pay — a payable invoice on any of the
					    visitor's accounts, or Account Funds deposits allowed (the
					    zero-balance "pay on account" case) — not merely whenever
					    payments are switched on. */}
					{canMakePayment && (
						<button
							type="button"
							onClick={() => {
								void navigate('/payments/make');
							}}
							className="mt-4 w-full px-4 py-2.5 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
						>
							{t('Make a Payment')}
						</button>
					)}
				</>
			)}
		</section>
	);
}

/**
 * PD-15869 — "How you're billed": one plain line per account saying when
 * money leaves, plus the next cycle charge when there is one.
 *
 * Everything the server sends is already resolved at the billing account,
 * so this component only has to choose sentences — it never re-derives
 * who bills whom.
 */
function BillingBlock({
	billing,
	t,
}: {
	billing: PortalBillingSnapshotDto;
	t: (key: string, params?: Record<string, string>) => string;
}): JSX.Element | null {
	const line = buildBillingLine(billing, t);
	const members = billing.accounts ?? [];

	// Nothing resolved and no per-account breakdown: say nothing rather
	// than render an empty heading.
	if (line == null && members.length === 0 && billing.nextCharge == null) {
		return null;
	}

	return (
		<div className="mt-4 pt-3 border-t border-brand-border">
			<p className="text-[11px] uppercase tracking-wide text-brand-muted">
				{t("How you're billed")}
			</p>

			{members.length > 0 ? (
				/* Ownership roll-up: the accounts are billed independently, so
				   one compact line each rather than one misleading summary. */
				<ul className="mt-1 space-y-0.5">
					{members.map((member) => (
						<li
							key={member.accountId ?? member.accountName}
							className="text-xs text-default"
						>
							<span className="font-medium">
								{t(member.accountName ?? '—')}
							</span>
							{t(': ')}
							{/* Already translated by the builder — wrapping it again
							    would look up a composed sentence as a key. */}
							{buildBillingLine(member, t) ?? t('—')}
						</li>
					))}
				</ul>
			) : (
				line != null && <p className="mt-1 text-xs text-default">{line}</p>
			)}

			{/* Billed with parent: the customer still needs the date their
			    parent's cycle runs, even though the charge itself is not
			    theirs to see. */}
			{billing.billedThrough?.nextDate != null && (
				<p className="mt-0.5 text-xs text-brand-muted">
					{t('Next cycle')}
					{t(': ')}
					{t(formatBillingDate(billing.billedThrough.nextDate) ?? '—')}
				</p>
			)}

			{billing.nextCharge != null && (
				<div className="mt-2">
					<p className="text-[11px] uppercase tracking-wide text-brand-muted">
						{t('Next charge')}
					</p>
					<p className="mt-0.5 text-xs text-default">
						{buildNextChargeLine(billing.nextCharge, t, (amount) =>
							formatCurrency(amount, t),
						)}
					</p>
					{billing.nextCharge.statementId != null && (
						<Link
							to={`/statements/${billing.nextCharge.statementId}`}
							className="mt-0.5 inline-block text-xs font-medium text-portal-accent hover:underline"
						>
							{t('View statement')}
						</Link>
					)}
				</div>
			)}
		</div>
	);
}

function MetricBlock({
	label,
	value,
	sublabel,
	valueClassName,
}: {
	label: string;
	value: string;
	sublabel?: string;
	/** Overrides the value's color (e.g. red for a past-due amount). */
	valueClassName?: string;
}): JSX.Element {
	return (
		<div>
			<p className="text-[11px] uppercase tracking-wide text-brand-muted">
				{label}
			</p>
			<p
				className={`mt-0.5 text-lg md:text-xl font-bold ${valueClassName ?? 'text-default'}`}
			>
				{value}
			</p>
			{sublabel != null && (
				<p className="text-[11px] text-brand-muted mt-0.5">{sublabel}</p>
			)}
		</div>
	);
}

function EmptyMessage({ message }: { message: string }): JSX.Element {
	return (
		<div className="flex-1 flex items-center justify-center text-center px-4 py-6">
			<p className="text-sm text-brand-muted max-w-md">{message}</p>
		</div>
	);
}

function formatCurrency(amount: number, t: (s: string) => string): string {
	const sign = amount < 0 ? '-' : '';
	const absAmount = Math.abs(amount);
	return `${sign}${t('$')}${absAmount.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}
